import { useEffect, useState, FormEvent } from 'react'
import { useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import rlsvoteApi from './rlsvoteApi'

type Candidato = { id: string; nome: string }
type ElezionePubblica = { ragione_sociale: string; stato: string; candidati: Candidato[] }

type Step = 'dati' | 'voto' | 'conferma'

export default function VotoPubblico() {
  const { slug } = useParams<{ slug: string }>()
  const [loading, setLoading] = useState(true)
  const [errore, setErrore] = useState<string | null>(null)
  const [elezione, setElezione] = useState<ElezionePubblica | null>(null)

  const [step, setStep] = useState<Step>('dati')
  const [nome, setNome] = useState('')
  const [cognome, setCognome] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [sessioneToken, setSessioneToken] = useState<string | null>(null)
  const [candidatoScelto, setCandidatoScelto] = useState<string | 'bianca' | null>(null)
  const [invio, setInvio] = useState(false)

  useEffect(() => {
    if (!slug) return
    rlsvoteApi
      .get<ElezionePubblica>(`/${slug}`)
      .then((res) => setElezione(res.data))
      .catch(() => setErrore('Elezione non trovata o non ancora aperta al voto.'))
      .finally(() => setLoading(false))
  }, [slug])

  async function handlePartecipa(e: FormEvent) {
    e.preventDefault()
    if (!file) {
      toast.error('Carica una foto del documento d\'identità')
      return
    }
    setInvio(true)
    try {
      const formData = new FormData()
      formData.append('nome', nome)
      formData.append('cognome', cognome)
      formData.append('file', file)
      const res = await rlsvoteApi.post<{ sessione_token: string }>(
        `/${slug}/partecipa`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      )
      setSessioneToken(res.data.sessione_token)
      setStep('voto')
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Errore durante la registrazione')
    } finally {
      setInvio(false)
    }
  }

  async function handleVota(e: FormEvent) {
    e.preventDefault()
    if (candidatoScelto === null || !sessioneToken) return
    setInvio(true)
    try {
      await rlsvoteApi.post(`/${slug}/vota`, {
        sessione_token: sessioneToken,
        candidato_id: candidatoScelto === 'bianca' ? null : candidatoScelto,
      })
      setStep('conferma')
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Errore durante il voto — se il problema persiste contatta la commissione')
    } finally {
      setInvio(false)
    }
  }

  if (loading) return <div className="p-8 text-center">Caricamento...</div>
  if (errore || !elezione) return <div className="p-8 text-center text-red-600">{errore}</div>

  return (
    <div className="max-w-md mx-auto p-6">
      <h1 className="text-xl font-bold mb-1">Elezione RLS</h1>
      <p className="text-sm text-gray-500 mb-6">{elezione.ragione_sociale}</p>

      {step === 'dati' && (
        <form onSubmit={handlePartecipa} className="space-y-4">
          <input className="input w-full" placeholder="Nome" value={nome} onChange={(e) => setNome(e.target.value)} required />
          <input className="input w-full" placeholder="Cognome" value={cognome} onChange={(e) => setCognome(e.target.value)} required />
          <div>
            <label className="label">Foto documento d'identità (jpg, png o pdf, max 10MB)</label>
            <input
              type="file"
              accept="image/jpeg,image/png,application/pdf"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              required
            />
          </div>
          <button type="submit" className="btn-primary w-full" disabled={invio}>
            {invio ? 'Invio in corso...' : 'Continua'}
          </button>
        </form>
      )}

      {step === 'voto' && (
        <form onSubmit={handleVota} className="space-y-3">
          <p className="font-medium mb-2">Seleziona un candidato:</p>
          {elezione.candidati.map((c) => (
            <label key={c.id} className="flex items-center gap-2 p-2 border rounded cursor-pointer">
              <input
                type="radio"
                name="candidato"
                value={c.id}
                checked={candidatoScelto === c.id}
                onChange={() => setCandidatoScelto(c.id)}
              />
              {c.nome}
            </label>
          ))}
          <label className="flex items-center gap-2 p-2 border rounded cursor-pointer">
            <input
              type="radio"
              name="candidato"
              value="bianca"
              checked={candidatoScelto === 'bianca'}
              onChange={() => setCandidatoScelto('bianca')}
            />
            Scheda bianca
          </label>
          <button type="submit" className="btn-primary w-full" disabled={invio || candidatoScelto === null}>
            {invio ? 'Invio in corso...' : 'Conferma voto'}
          </button>
        </form>
      )}

      {step === 'conferma' && (
        <div className="text-center p-6">
          <p className="text-lg font-semibold text-green-700">Voto registrato con successo.</p>
          <p className="text-sm text-gray-500 mt-2">Grazie per aver partecipato.</p>
        </div>
      )}
    </div>
  )
}
